import { useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import JsonLd from '@/components/base/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { allVideos } from '@/mocks/videos';
import { siteInfo } from '@/mocks/siteInfo';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredVideos = useMemo(() => {
    let videos = allVideos.filter((v) => v.categorySlug === slug);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      videos = videos.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          v.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (videos.length === 0 && !searchQuery) {
      videos = allVideos;
    }
    return videos;
  }, [slug, searchQuery]);

  if (!category) {
    return <NotFound />;
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 91视频网站`,
    description: category.description,
    url: `${siteInfo.url}/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 91视频免费在线观看`}
        description={`91视频网站${category.name}频道，${category.description}。共${category.count}部${category.name}视频免费在线观看，高清流畅。`}
        keywords={`91视频,91${category.name},91在线${category.name},${category.name}免费观看,${category.name}在线播放,91${category.name}视频`}
        canonicalUrl={`${siteInfo.url}/category/${category.slug}`}
        ogTitle={`${category.name} - 91视频免费在线观看`}
        ogDescription={category.description}
        ogType="website"
      />
      <JsonLd data={categorySchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <section className="py-8 md:py-12 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading flex items-center gap-3">
                    <i className={`${category.icon} text-primary-500`}></i>
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-2">
                    {category.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">
                    共 <strong className="text-primary-600">{filteredVideos.length}</strong> 部视频
                  </span>
                  {searchQuery && (
                    <span className="text-sm text-foreground-500 bg-background-200 px-3 py-1 rounded-full">
                      搜索: {searchQuery}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-8">
                <Link
                  to={`/category/${slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    !searchQuery
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  全部
                </Link>
                {categories.filter(c => c.slug !== slug).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop="">
                  {filteredVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      id={video.id}
                      title={video.title}
                      slug={video.slug}
                      thumbnail={video.thumbnail}
                      duration={video.duration}
                      category={video.category}
                      categorySlug={video.categorySlug}
                      views={video.views}
                      rating={video.rating}
                      isHot={video.isHot}
                      isNew={video.isNew}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">没有找到相关视频，试试其他关键词吧</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}